import NetworkInterface from "./NetworkInterface";

export default class Api {
  /**
   * @param {{ networkInterface: NetworkInterface }} options
   */
  constructor({ networkInterface } = {}) {
    if (!(networkInterface instanceof NetworkInterface)) {
      throw new Error("The Api class must be provided a NetworkInterface");
    }
    this.networkInterface = networkInterface;
  }

  async getDomainsByType(keyword, type, limit) {
    // Types:
    // HERO
    // POPULAR
    // SUGGESTIONS
    // ALL
    // COUNTRY

    const res = await this.networkInterface.get(
      `/rest/domains/${keyword}?filter=${type}&limit=${limit}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );

    return await res.json();
  }

  async getNextDomainsBatch(nextUrl) {
    const res = await this.networkInterface.get(nextUrl, {
      headers: {
        Pragma: "no-cache",
      },
    });

    return await res.json();
  }

  async getBundleByType(keyword, type) {
    // Types:
    // POPULAR
    // SUGGESTIONS
    // COUNTRY

    const res = await this.networkInterface.get(
      `/rest/domains/${keyword}/bundles?filter=${type}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );

    return await res.json();
  }

  async addDomain(domain, tld, suggested) {
    const res = await this.networkInterface.get(
      `/ajax-add-domain.do?extraDomainName=${domain}&extraDomainSuffix=${tld}&suggestedDomain=${suggested}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );
    return await res.json();
  }

  async showRemoveDomainWarning(domain, tld) {
    return tld === ".one";
  }

  async removeDomain(domain, tld) {
    const res = await this.networkInterface.get(
      `/ajax-remove-domain.do?extraDomainName=${domain}"&extraDomainSuffix=${tld}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );
    return await res.json();
  }

  async transferDomain(domain, tld, authCode) {
    // eslint-disable-next-line no-undef
    let formData = new FormData();
    formData.append("extraDomainName", domain);
    formData.append("extraDomainSuffix", tld);

    let url = "/ajax-add-transfer-domain.do?move=true";

    if (authCode) {
      url += `&extraDomainRegistrationCode=${authCode}`;
    }

    const res = await this.networkInterface.postform(url, formData);
    return await res.json();
  }

  async getCart(countryCode) {
    const url = new URL(
      window.location.protocol +
        window.location.host +
        "/ajax-price-calculation-json.do",
    );

    url.searchParams.set("customerCountryCode", countryCode);

    const res = await this.networkInterface.get(url.toString(), {
      headers: {
        Pragma: "no-cache",
      },
    });
    return await res.json();
  }

  async getHostingPackages() {
    const url = "/ajax-new-size-price.do?includeCurrency=false";

    const res = await this.networkInterface.get(url, {
      headers: {
        Pragma: "no-cache",
      },
    });
    return await res.json();
  }

  async selectPackage(hostingPackageId) {
    const res = await this.networkInterface.put(
      `/rest/basket/plan/?webSpaceSize=${hostingPackageId}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );
    return await res;
  }

  async removePlanFromDomain(domain) {
    const res = await this.networkInterface.put(
      `/rest/basket/domains/${domain}/removePlan`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );
    return await res;
  }

  async getUpsellPackages(hostingPackageId, numberOfDomainsInCart = 1) {
    const res = await this.networkInterface.get(
      `/rest/basket/upsell/plans/${hostingPackageId}/${numberOfDomainsInCart}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );
    return await res.json();
  }

  async getHostingPlanPrice(hostingPlanId) {
    const res = await this.networkInterface.get(
      `/rest/plans/${hostingPlanId}/prices`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );
    return await res.json();
  }

  async getUpsellProducts(hostingPackageId, numberOfDomainsInCart = 1) {
    const res = await this.networkInterface.get(
      `/rest/plans/${hostingPackageId}/upsell?nofDomains=${numberOfDomainsInCart}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );
    const response = await res.json();

    /**
     * For debugging when there is no response from the server:
     */

    /*
    const response = { result: {} };

    const getRandom = (arr, n) => {
      const result = new Array(n);
      let len = arr.length;
      const taken = new Array(len);

      if (n > len) {
        throw new RangeError("getRandom: more elements taken than available");
      }

      while (n--) {
        var x = Math.floor(Math.random() * len);
        result[n] = arr[x in taken ? taken[x] : x];
        taken[x] = --len in taken ? taken[len] : len;
      }
      return result;
    };

    response.result.plans = ["PPS_XXLARGE"];

    const tmpAddons = [
      "WEBSITE_BUILDER_PREMIUM",
      "WEBSHOP",
      "BACKUP_RESTORE",
      "SITELOCK",
      "MARKETGOO_START",
      "UBERALL"
    ];

    response.result.addOnProducts = getRandom(tmpAddons, 1);
    */

    return response;
  }

  async getPackageCaps() {
    const res = await this.networkInterface.get("/rest/plans/features", {
      headers: {
        Pragma: "no-cache",
      },
    });
    return await res.json();
  }

  async getAddonProductPrice(addonProductId) {
    const url = `/rest/products/addons/${addonProductId}/pricesAsStrings?includeCurrency=false&rebateType=STORE_FRONT`;

    const res = await this.networkInterface.get(url, {
      headers: {
        Pragma: "no-cache",
      },
    });

    return await res.json();
  }

  async addAddonProduct(addonProduct, domain) {
    const res = await this.networkInterface.put(
      `/rest/basket/domains/${domain}/addonProducts`,
      {
        addonProduct: addonProduct,
      },
    );
    return await res;
  }

  async removeAddonProduct(addonProduct, domain) {
    const res = await this.networkInterface.delete(
      `/rest/basket/domains/${domain}/addonProducts`,
      {
        addonProduct: addonProduct,
      },
    );
    return await res;
  }

  async removeAddonProductWithoutDomain(addonProduct) {
    const res = await this.networkInterface.delete(
      "/rest/basket/addonProduct",
      addonProduct,
    );
    return await res;
  }

  async updateAddonProductsForm(addonProducts) {
    // eslint-disable-next-line no-undef
    let formData = new FormData();
    formData.append("addOnProducts", JSON.stringify(addonProducts));
    const res = await this.networkInterface.postform(
      "/ajax-add-addon-product.do",
      formData,
    );
    return await res.json();
  }

  async postVoucherCode(voucherCode) {
    const res = await this.networkInterface.put(
      `/rest/basket/couponCode?couponCode=${voucherCode}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );
    return await res;
  }

  async getPopularTlds(countryCode, locale) {
    const res = await this.networkInterface.get(
      `/rest/tlds/popular?country=${countryCode}&locale=${locale}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );

    return await res.json();
  }

  async getTldPrice(tld, countryCode) {
    const res = await this.networkInterface.get(
      `/rest/tlds/${tld}?country=${countryCode}`,
      {
        headers: {
          Pragma: "no-cache",
        },
      },
    );

    return await res.json();
  }

  async getVPSAddons() {
    const res = await this.networkInterface.get("/rest/onevps/upsell", {
      headers: {
        Pragma: "no-cache",
      },
    });

    return await res.json();
  }

  async addVPS(vpsProduct) {
    const res = await this.networkInterface.put(
      `/rest/basket/vps-product?vpsProduct=${vpsProduct}`,
    );

    return res.ok;
  }

  async removeVPS(vpsProduct) {
    const res = await this.networkInterface.delete(
      `/rest/basket/vps-product?vpsProduct=${vpsProduct}`,
    );

    return res.ok;
  }

  async getVPSPrices(vpsProduct) {
    const res = await this.networkInterface.get(
      `/rest/onevps/products/${vpsProduct}/prices`,
    );

    return await res.json();
  }

  async getCustomerInformationFields(
    customerType,
    countryCode,
    tlds,
    isRegistrant,
    citizenship,
    includeOptions,
  ) {
    const citizenshipSearchParam = citizenship
      ? `citizenship=${citizenship}`
      : "";

    const url = `/rest/customer/required-fields?customerType=${customerType}&country=${countryCode}&tlds=${tlds}&isRegistrant=${isRegistrant}&includeOptions=${includeOptions}&${citizenshipSearchParam}`;

    const res = await this.networkInterface.get(url, {
      headers: {
        Pragma: "no-cache",
      },
    });

    return await res.json();
  }

  async saveCustomerInformation(customerData) {
    const res = await this.networkInterface.post(
      `/rest/customer/submit/customer`,
      { ...customerData },
    );

    return await res.json();
  }

  async saveRegistrantInformation(registrantData, isCustomerSame) {
    const res = await this.networkInterface.post(
      `/rest/customer/submit/registrant?customerIsSame=${isCustomerSame}`,
      { ...registrantData },
    );

    return await res.json();
  }

  async checkifUserExists(email, fullName, companyName) {
    const res = await this.networkInterface.get(
      `/rest/customer/existing?name=${fullName}&email=${email}&companyName=${companyName}`,
    );

    return await res.json();
  }

  async getUserDataFromSession() {
    const res = await this.networkInterface.get("/rest/customer/on-session");

    return await res.json();
  }

  async getTerms(tlds, country) {
    const res = await this.networkInterface.get(
      `/rest/tlds/terms?${tlds}&registration=true&country=${country}`,
    );

    return await res.json();
  }

  async authenticateUser() {
    const res = await this.networkInterface.get(
      "/admin/api/session/customerInfo",
      {
        // To prevent from redirection to Keycloak
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      },
    );

    return await res.json();
  }

  async getPaymentTypes(countryCode, customerType, customerEmail) {
    const orderflowContext = "ORDER_FLOW";

    const res = await this.networkInterface.get(
      `/rest/orderflow/paymentTypes?context=${orderflowContext}&customerType=${customerType}&country=${countryCode}&customerEmail=${customerEmail}`,
    );

    return await res.json();
  }

  async getCountryRestrictions(countryCode) {
    const res = await this.networkInterface.get(
      `/rest/orderflow/countryRestrictions?country=${countryCode}`,
    );

    return await res.json();
  }

  async submitPayment(
    preOrderCode,
    saveRecurringPaymentInfo,
    marketingCommunication,
    paymentType,
    iban,
    ibanAccountHolder,
    cfp,
  ) {
    let url = `/ajax-hosted-payment-json.do?preOrderCode=${preOrderCode}&saveRecurringPaymentInfo=${saveRecurringPaymentInfo}&marketingCommunication=${marketingCommunication}&paymentType=${paymentType}&cfp=${cfp}`;
    if (iban && ibanAccountHolder) {
      url = `${url}&iban=${iban}&ibanAccountHolder=${ibanAccountHolder}`;
    }

    const res = await this.networkInterface.post(url);
    return await res.json();
  }

  async saveOrderFlowData(orderFlowData) {
    const res = await this.networkInterface.put(
      `/rest/customer/submit/orderFlowData`,
      { ...orderFlowData },
    );

    return await res.json();
  }

  async getPaymentError(orderCode) {
    const res = await this.networkInterface.get(
      `/rest/customer/paymentError?orderCode=${orderCode}`,
    );

    return await res.json();
  }

  async checkIfInternalIp() {
    const res = await this.networkInterface.get("/rest/customer/internal-ip");

    return await res.json();
  }

  async loadPreOrder(preorderCode) {
    const res = await this.networkInterface.get(
      `/rest/customer/preorder/${preorderCode}`,
    );

    return await res.json();
  }

  async clearCustomerData(type) {
    const res = await this.networkInterface.delete(
      `/rest/customer/clear-customer-data?type=${type}`,
    );

    return await res.json();
  }

  async loadCommunicationPreferences(adminEmail) {
    const res = await this.networkInterface.get(
      `/admin/api/session/admin/${adminEmail}/communication-preferences`,
    );

    return await res.json();
  }

  async getEidError() {
    const res = await this.networkInterface.get(`/rest/customer/eidError`);

    return await res.json();
  }

  async getActivationType(orderCode) {
    const res = await this.networkInterface.get(
      `/rest/orderflow/activationTypes?orderCode=${orderCode}`,
    );

    return await res.json();
  }

  async getStatisticsData() {
    const res = await this.networkInterface.get(
      "/admin/api/session/customerStatisticsData",
      {
        // To prevent from redirection to Keycloak
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      },
    );

    return await res.json();
  }

  async updateDIFMConsultInCart(isRequested) {
    const res = await this.networkInterface.put(
      `/rest/basket/difmConsult?isRequested=${isRequested}`,
    );
    return await res;
  }

  async getPendingDataLayerRequests() {
    const res = await this.networkInterface.get(
      `/rest/customer/session/pending-datalayer-requests`,
    );

    return await res.json();
  }

  async markPendingDataLayerRequestsSent() {
    const res = await this.networkInterface.put(
      `/rest/customer/session/pending-datalayer-requests`,
    );

    return await res.json();
  }
}
